import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Tooltip } from 'react-tooltip';
import type { CaseOption } from './CasePicker';
import styles from './ProjectCasesList.module.scss';

const TOOLTIP_ID = 'project-cases-list-tooltip';
const PANEL_ID = 'project-cases-panel';

interface ProjectCasesListProps {
  cases: CaseOption[];
  onRemove: (id: number) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const SKELETON_WIDTHS = ['68%', '85%', '55%', '78%', '90%'];

function SkeletonItem() {
  return (
    <li className={styles.skeletonItem} aria-hidden="true">
      <span className={styles.skeletonText} />
      <span className={styles.skeletonCircle} />
    </li>
  );
}

function RemoveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function ProjectCasesList({
  cases,
  onRemove,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: ProjectCasesListProps) {
  const { t } = useTranslation();

  if (cases.length === 0) {
    return (
      <div className={styles.panel} aria-live="polite" aria-busy={isLoadingMore}>
        {isLoadingMore ? (
          <ul className={styles.list} role="list">
            {SKELETON_WIDTHS.map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i} className={styles.skeletonItem} aria-hidden="true">
                <span className={styles.skeletonText} style={{ width: w }} />
                <span className={styles.skeletonCircle} />
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>{t('No cases in the project yet')}</p>
        )}
      </div>
    );
  }

  return (
    <div id={PANEL_ID} className={styles.panel}>
      <InfiniteScroll
        dataLength={cases.length}
        next={onLoadMore ?? (() => {})}
        hasMore={hasMore}
        loader={
          <ul className={styles.list}>
            <SkeletonItem />
            <SkeletonItem />
          </ul>
        }
        scrollableTarget={PANEL_ID}
        style={{ overflow: 'visible' }}
      >
        <ul className={styles.list} role="list" aria-label={t('Added cases')}>
          {cases.map((c) => (
            <li key={c.value} className={styles.item}>
              <span className={styles.name}>{c.label}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => onRemove(c.value)}
                aria-label={t('Remove {{name}}', { name: c.label })}
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={t('Remove')}
              >
                <RemoveIcon />
              </button>
            </li>
          ))}
        </ul>
      </InfiniteScroll>
      <Tooltip id={TOOLTIP_ID} place="top" />
    </div>
  );
}
